import {
  RECHARGE_WEBHOOK_ORIGIN,
  type RechargeRegisteredWebhook,
  type RechargeRegisteredWebhooks,
  type RechargeSettingsSummary,
} from '@flux/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MoreHorizontal, Trash2 } from 'lucide-react';
import { useState, type FormEvent, type JSX } from 'react';
import { PageSection } from '@/components/layout/page.js';
import { StatusBadge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { ConfirmDialog, useConfirmDialog } from '@/components/ui/confirm-dialog.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.js';
import { ErrorState } from '@/components/ui/error-state.js';
import { Field } from '@/components/ui/field.js';
import { Input } from '@/components/ui/input.js';
import { SaveButton } from '@/components/ui/save-button.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table.js';
import { ApiError } from '@/lib/api/client.js';
import { queryKeys } from '@/lib/query/keys.js';
import { toast } from '@/lib/toast.js';
import { deleteRechargeWebhook, fetchRechargeWebhooks, registerMissingRechargeWebhooks } from './api-recharge.js';
import { SecretField } from './recharge-parts.js';
import { SectionTable } from './settings-parts.js';
import { useSaveRechargeSettings } from './use-save-recharge-settings.js';

const SECRET_REQUIRED_ID = 'recharge-register-secret-required';

function createdMessage(count: number): string {
  if (count === 0) {
    return 'Aucun topic à enregistrer';
  }
  return count === 1 ? '1 topic enregistré chez Recharge' : `${String(count)} topics enregistrés chez Recharge`;
}

function copyUrl(url: string): void {
  // Copie dans le geste utilisateur, sans `await` avant l'écriture.
  navigator.clipboard.writeText(url).then(
    () => {
      toast.success('URL copiée');
    },
    () => {
      toast.error('Impossible de copier l’URL. Sélectionnez-la puis copiez-la.');
    },
  );
}

function WebhooksTableSkeleton(): JSX.Element {
  return (
    <div aria-hidden="true" className="flex flex-col gap-2 py-3">
      {[0, 1, 2].map((index) => (
        <Skeleton key={index} className="mx-4 h-8" />
      ))}
    </div>
  );
}

function WebhooksTable({
  items,
  onDelete,
}: {
  items: readonly RechargeRegisteredWebhook[];
  onDelete: (webhook: RechargeRegisteredWebhook) => void;
}): JSX.Element {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Topic</TableHead>
          <TableHead>URL</TableHead>
          <TableHead>Provenance</TableHead>
          <TableHead className="w-12">
            <span className="sr-only">Actions</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((webhook) => (
          <TableRow key={webhook.id}>
            <TableCell className="whitespace-nowrap font-mono text-xs" translate="no">
              {webhook.topic}
            </TableCell>
            <TableCell className="max-w-0 w-full">
              <span className="block truncate text-muted-foreground" title={webhook.address} translate="no">
                {webhook.address}
              </span>
            </TableCell>
            <TableCell>
              <StatusBadge registry={RECHARGE_WEBHOOK_ORIGIN} code={webhook.origin} />
            </TableCell>
            <TableCell className="text-right">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Actions pour le webhook ${webhook.topic}`}
                  >
                    <MoreHorizontal aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onSelect={() => {
                      onDelete(webhook);
                    }}
                  >
                    <Trash2 aria-hidden="true" />
                    Supprimer…
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function RegisterMissingControl({
  webhooks,
  secretConfigured,
}: {
  webhooks: RechargeRegisteredWebhooks;
  secretConfigured: boolean;
}): JSX.Element {
  const queryClient = useQueryClient();
  const register = useMutation({
    mutationFn: registerMissingRechargeWebhooks,
    onSuccess: (result) => {
      queryClient.setQueryData(queryKeys.rechargeSettings.webhooks(), result.webhooks);
      toast.success(createdMessage(result.created.length));
    },
    onError: (error: unknown) => {
      toast.fromError(error);
    },
  });
  const missing = webhooks.missingTopics;

  if (missing.length === 0) {
    return <p className="text-sm text-muted-foreground">Tous les topics des fonctions actives pointent vers Flux</p>;
  }

  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-sm text-muted-foreground">
        Topics manquants :{' '}
        <span className="font-mono text-xs text-foreground" translate="no">
          {missing.join(', ')}
        </span>
      </p>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <Button
          type="button"
          variant="outline"
          isLoading={register.isPending}
          aria-disabled={secretConfigured ? undefined : true}
          aria-describedby={secretConfigured ? undefined : SECRET_REQUIRED_ID}
          className="aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
          onClick={() => {
            if (secretConfigured) {
              register.mutate();
            }
          }}
        >
          Enregistrer les topics manquants ({missing.length})
        </Button>
        {secretConfigured ? null : (
          <p id={SECRET_REQUIRED_ID} className="text-xs text-muted-foreground">
            Ajoutez d’abord le client secret : sans lui, Flux refuserait chaque webhook reçu.
          </p>
        )}
      </div>
    </div>
  );
}

function RegisteredWebhooks({ settings }: { settings: RechargeSettingsSummary }): JSX.Element {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: queryKeys.rechargeSettings.webhooks(),
    queryFn: fetchRechargeWebhooks,
    enabled: settings.apiTokenConfigured,
  });
  const deleteDialog = useConfirmDialog<RechargeRegisteredWebhook>();
  const remove = useMutation({
    mutationFn: (webhook: RechargeRegisteredWebhook) => deleteRechargeWebhook(webhook.id),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.rechargeSettings.webhooks(), data);
      toast.success('Webhook supprimé');
      deleteDialog.close();
    },
    onError: (error: unknown) => {
      toast.fromError(error);
    },
  });

  let body: JSX.Element;
  if (!settings.apiTokenConfigured) {
    body = (
      <p className="text-sm text-muted-foreground">
        Ajoutez le token API dans la section Connexion pour lister les webhooks enregistrés chez Recharge.
      </p>
    );
  } else if (query.isPending) {
    body = (
      <SectionTable atEnd>
        <WebhooksTableSkeleton />
      </SectionTable>
    );
  } else if (query.isError) {
    body = (
      <ErrorState
        message={
          query.error instanceof ApiError ? query.error.message : 'Impossible de lire les webhooks enregistrés chez Recharge.'
        }
        detail={query.error instanceof ApiError ? query.error.detail : undefined}
        onRetry={() => {
          void query.refetch();
        }}
        className="py-6"
      />
    );
  } else {
    body = (
      <>
        <RegisterMissingControl webhooks={query.data} secretConfigured={settings.webhookSecretConfigured} />
        {query.data.items.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Aucun webhook enregistré chez Recharge. Enregistrez les topics manquants pour que Flux reçoive les événements.
          </p>
        ) : (
          <SectionTable atEnd>
            <WebhooksTable items={query.data.items} onDelete={deleteDialog.open} />
          </SectionTable>
        )}
      </>
    );
  }

  const target = deleteDialog.target;
  return (
    <div className="flex flex-col gap-3 border-t border-border pt-5">
      <div className="flex flex-col gap-0.5">
        <h3 className="text-sm font-semibold">Webhooks enregistrés chez Recharge</h3>
        <p className="text-xs text-muted-foreground">
          Provenance déduite de l’URL. Après le passage d’une fonction en Actif, supprimez ses webhooks de l’ancien
          serveur.
        </p>
      </div>
      {body}
      <ConfirmDialog
        open={deleteDialog.isOpen}
        onOpenChange={(open) => {
          if (!open && !remove.isPending) {
            deleteDialog.close();
          }
        }}
        title={target ? `Supprimer le webhook ${target.topic} ?` : ''}
        description={
          target ? (
            <>
              Recharge n’enverra plus cet événement à{' '}
              <span className="break-all" translate="no">
                {target.address}
              </span>
              .
            </>
          ) : (
            ''
          )
        }
        confirmLabel="Supprimer le webhook"
        isConfirming={remove.isPending}
        onConfirm={() => {
          if (target) {
            remove.mutate(target);
          }
        }}
      />
    </div>
  );
}

/**
 * Carte **Webhooks** (`06` §8.6, §1.3) : client secret qui vérifie la
 * signature, URL à déclarer chez Recharge, et webhooks enregistrés sur le
 * compte avec leurs deux actions (enregistrer les topics manquants,
 * supprimer).
 */
export function RechargeWebhooksSection({ settings }: { settings: RechargeSettingsSummary }): JSX.Element {
  const [secret, setSecret] = useState('');
  const save = useSaveRechargeSettings(() => {
    setSecret('');
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const webhookSecret = secret.trim();
    if (webhookSecret) {
      save.mutate({ webhookSecret });
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <PageSection
        title="Webhooks"
        description="Recharge prévient Flux de chaque paiement, adresse et abonnement par webhook."
        footer={<SaveButton isDirty={secret.trim() !== ''} isLoading={save.isPending} />}
      >
        <div className="flex flex-col gap-5">
          <SecretField
            id="recharge-webhook-secret"
            label="Client secret"
            configured={settings.webhookSecretConfigured}
            value={secret}
            onChange={setSecret}
            hint="Client secret de l’app Recharge, utilisé pour vérifier la signature de chaque webhook"
          />
          <Field label="URL des webhooks Flux" htmlFor="recharge-webhook-url">
            <div className="flex min-w-0 flex-wrap gap-2">
              <Input
                id="recharge-webhook-url"
                readOnly
                value={settings.webhookUrl}
                spellCheck={false}
                translate="no"
                className="min-w-0 max-w-md flex-1"
                onFocus={(event) => {
                  event.currentTarget.select();
                }}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  copyUrl(settings.webhookUrl);
                }}
              >
                Copier
              </Button>
            </div>
          </Field>
          <RegisteredWebhooks settings={settings} />
        </div>
      </PageSection>
    </form>
  );
}
